// @ts-nocheck
/**
 * <sticky-aside>
 *
 * Reusable cross-section sticky panel for detail templates (Location
 * metaobject, event product, etc.).
 *
 * The wrapper is always position:absolute (anchored to <main>); the panel
 * inside is always position:sticky. We never swap layout modes — we only
 * adjust the wrapper's height so sticky knows when to release. This avoids
 * the width-recalculation jumps that pure fixed/absolute toggling
 * implementations suffer from.
 */

const MOBILE_QUERY = '(max-width: 749px)';
const FALLBACK_MIN_HEIGHT = '600px';

class StickyAside extends HTMLElement {
  /** @type {HTMLElement | null} */
  #wrapper = null;
  /** @type {HTMLElement | null} */
  #endTarget = null;
  /** @type {HTMLElement | null} */
  #mainEl = null;
  /** @type {ResizeObserver | null} */
  #mainResizeObserver = null;
  /** @type {ResizeObserver | null} */
  #panelResizeObserver = null;
  /** @type {MediaQueryList | null} */
  #mobileMQ = null;
  /** @type {() => void} */
  #onMQChange = () => {};
  /** @type {() => void} */
  #onResize = () => {};
  /** @type {number | null} */
  #recomputeRafId = null;

  connectedCallback() {
    // In the Theme Customiser, the section renders inline (overridden by CSS
    // .shopify-design-mode rules). We don't manage sticky bounds in design
    // mode either — the merchant should see static positioning while editing.
    if (window.Shopify && window.Shopify.designMode) return;

    this.#wrapper = this.querySelector('.sticky-aside__wrapper');
    if (!this.#wrapper) return;

    this.#mainEl = document.querySelector('main') || document.getElementById('MainContent');

    this.#mobileMQ = window.matchMedia(MOBILE_QUERY);
    this.#onMQChange = () => this.#evaluate();
    if (this.#mobileMQ.addEventListener) {
      this.#mobileMQ.addEventListener('change', this.#onMQChange);
    } else if (this.#mobileMQ.addListener) {
      // Safari < 14 fallback
      this.#mobileMQ.addListener(this.#onMQChange);
    }

    this.#onResize = () => this.#scheduleRecompute();
    window.addEventListener('resize', this.#onResize, { passive: true });

    if (this.#mainEl && 'ResizeObserver' in window) {
      this.#mainResizeObserver = new ResizeObserver(() => this.#scheduleRecompute());
      this.#mainResizeObserver.observe(this.#mainEl);
    }

    if ('ResizeObserver' in window) {
      this.#panelResizeObserver = new ResizeObserver(() => this.#scheduleRecompute());
      const panel = this.querySelector('.sticky-aside__panel');
      if (panel) this.#panelResizeObserver.observe(panel);
    }

    this.#evaluate();
  }

  disconnectedCallback() {
    if (this.#mobileMQ) {
      if (this.#mobileMQ.removeEventListener) {
        this.#mobileMQ.removeEventListener('change', this.#onMQChange);
      } else if (this.#mobileMQ.removeListener) {
        this.#mobileMQ.removeListener(this.#onMQChange);
      }
    }
    window.removeEventListener('resize', this.#onResize);
    if (this.#mainResizeObserver) this.#mainResizeObserver.disconnect();
    if (this.#panelResizeObserver) this.#panelResizeObserver.disconnect();
    if (this.#recomputeRafId !== null) cancelAnimationFrame(this.#recomputeRafId);
  }

  #evaluate() {
    if (!this.#wrapper) return;

    if (this.#mobileMQ && this.#mobileMQ.matches) {
      // Mobile: clear any previously-set height so the panel renders inline
      this.#wrapper.style.removeProperty('--aside-wrapper-height');
      this.#wrapper.style.removeProperty('--aside-wrapper-min-height');
      return;
    }

    this.#scheduleRecompute();
  }

  #scheduleRecompute() {
    if (this.#recomputeRafId !== null) return;
    this.#recomputeRafId = requestAnimationFrame(() => {
      this.#recomputeRafId = null;
      this.#recomputeBounds();
    });
  }

  #findEndTarget() {
    const selector = (this.dataset.endTarget || '').trim();
    if (selector) {
      try {
        const el = document.querySelector(selector);
        if (el instanceof HTMLElement) return el;
      } catch (e) {
        // Bad selector — fall through to fallback
      }
    }

    // Fallback: last shopify-section in <main>
    if (this.#mainEl) {
      const sections = this.#mainEl.querySelectorAll(':scope > .shopify-section');
      const last = sections[sections.length - 1];
      if (last instanceof HTMLElement) {
        return last;
      }
    }
    return null;
  }

  #recomputeBounds() {
    if (!this.#wrapper || !this.#mainEl) return;
    if (this.#mobileMQ && this.#mobileMQ.matches) return;

    this.#endTarget = this.#findEndTarget();
    if (!this.#endTarget) {
      // No end target available — give it a tall fallback so sticky works
      this.#wrapper.style.setProperty('--aside-wrapper-height', FALLBACK_MIN_HEIGHT);
      return;
    }

    const wrapperRect = this.#wrapper.getBoundingClientRect();
    const endRect = this.#endTarget.getBoundingClientRect();
    const wrapperTop = wrapperRect.top + window.scrollY;
    const endTop = endRect.top + window.scrollY;
    const bottomOffset = parseInt(this.dataset.bottomOffset || '0', 10) || 0;
    const height = Math.max(0, Math.floor(endTop - wrapperTop - bottomOffset));

    if (height > 0) {
      this.#wrapper.style.setProperty('--aside-wrapper-height', height + 'px');
    } else {
      this.#wrapper.style.removeProperty('--aside-wrapper-height');
    }
  }
}

if (!customElements.get('sticky-aside')) {
  customElements.define('sticky-aside', StickyAside);
}
